import { useTrackingHelpers } from "@/hooks/useTrackingHelpers";
import { OptimizedImage } from "@/components/OptimizedImage";
import { cn } from "@/lib/utils";
import { APP_STORE_LINKS } from "@/utils/promotional-links";

interface AppStoreBadgesProps {
  className?: string;
  direction?: "row" | "column";
  size?: "sm" | "md" | "lg";
  href?: string; // Optional override for badge href (defaults to /app)
}

export function AppStoreBadges({
  className = "",
  direction = "row",
  size = "md",
  href = "/app?src=website",
}: AppStoreBadgesProps) {
  const { trackAppDownload } = useTrackingHelpers();
  const goTo = (href: string) => (window.location.href = href);

  const containerClasses = cn(
    "flex gap-4",
    direction === "row" ? "flex-row" : "flex-col",
    className,
  );

  const APP_STORE_URL = APP_STORE_LINKS.APPLE;
  const PLAY_STORE_URL = APP_STORE_LINKS.GOOGLE_PLAY;

  const badgeDimensions = {
    sm: { height: 40, appStoreWidth: 120, googlePlayWidth: 135 },
    md: { height: 48, appStoreWidth: 144, googlePlayWidth: 162 },
    lg: { height: 64, appStoreWidth: 192, googlePlayWidth: 216 },
  } as const;

  const dimensions = badgeDimensions[size];

  const detectDevice = (): "ios" | "android" | "desktop" => {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return "ios";
    if (/android/i.test(ua)) return "android";
    return "desktop";
  };

  // Location will be handled on /app for mobile devices

  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    platform: "ios" | "android" | "auto",
  ) => {
    e.preventDefault();
    try {
      const device = detectDevice();
      trackAppDownload(
        (platform === "auto" ? device : platform) as any,
        "website",
      );
    } catch {}

    // If href is a custom internal route (like /driver), always redirect there
    if (href.startsWith("/")) {
      goTo(href);
      return;
    }

    // Otherwise, use device-specific app store URLs
    const device = platform === "auto" ? detectDevice() : platform;
    if (device === "ios") {
      goTo(APP_STORE_URL);
    } else if (device === "android") {
      goTo(PLAY_STORE_URL);
    } else {
      // desktop: keep user on internal landing
      goTo("/app?src=website");
    }
  };

  return (
    <div className={containerClasses}>
      {/* Auto badge: choose best store for device */}
      <a
        href={href}
        className="inline-block hover:opacity-80 transition-opacity"
        aria-label="Download the Tigola app"
        onClick={(e) => handleClick(e, "auto")}
      >
        <OptimizedImage
          src="/app-store-badge.webp"
          alt="Download on the App Store"
          width={dimensions.appStoreWidth}
          height={dimensions.height}
          className="inline-block"
          deriveCandidates={false}
        />
      </a>

      {/* Google Play Badge */}
      <a
        href={href}
        className="inline-block hover:opacity-80 transition-opacity"
        aria-label="Get it on Google Play"
        onClick={(e) => handleClick(e, "android")}
      >
        <OptimizedImage
          src="/google-play-badge.webp"
          alt="Get it on Google Play"
          width={dimensions.googlePlayWidth}
          height={dimensions.height}
          className="inline-block"
          deriveCandidates={false}
        />
      </a>
    </div>
  );
}
